import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import { Json } from '../types/supabase';
import { getChatbotPublicId } from '../utils/getChatbotPublicId';

// Type for the chatbot_configs table
export interface ChatbotConfig {
  id: string;
  chatbot_public_id: string;
  colors?: Json;
  texts?: Json;
  quick_actions?: Json;
  mini_apps?: Json;
  button_colors?: Json;
  logos?: Json;
  calendly_config?: Json;
  ai_settings?: Json;
  multilingual_settings?: Json;
  widget_settings?: Json;
  theme_settings?: Json;
  order_tracking?: Json;
  created_at: string;
  updated_at: string;
}

interface ChatbotConfigState {
  configs: Record<string, ChatbotConfig>; // Indexed by chatbot_public_id
  isLoading: boolean;
  error: Error | null;
  
  // Actions
  fetchConfig: (chatbotPublicId?: string, forceRefresh?: boolean) => Promise<ChatbotConfig | null>;
  getConfigSection: <T>(
    sectionKey: keyof Omit<ChatbotConfig, 'id' | 'chatbot_public_id' | 'created_at' | 'updated_at'>,
    chatbotPublicId?: string,
    forceRefresh?: boolean
  ) => Promise<T | null>;
  updateConfigSection: <T extends object | string | number | boolean | null>(
    sectionKey: keyof Omit<ChatbotConfig, 'id' | 'chatbot_public_id' | 'created_at' | 'updated_at'>,
    data: T,
    chatbotPublicId?: string
  ) => Promise<boolean>;
  clearConfig: (chatbotPublicId?: string) => void;
}

export const useChatbotConfigStore = create<ChatbotConfigState>((set, get) => ({
  configs: {},
  isLoading: false,
  error: null,
  
  fetchConfig: async (chatbotPublicId, forceRefresh = false) => {
    try {
      // Get chatbotPublicId if not provided - fixed the || operation that was causing the issue
      let effectiveChatbotId: string;
      
      if (chatbotPublicId) {
        effectiveChatbotId = chatbotPublicId;
      } else {
        const fetchedId = await getChatbotPublicId();
        if (!fetchedId) {
          console.error('No chatbotPublicId available for config fetch');
          return null;
        }
        effectiveChatbotId = fetchedId;
      }
      
      // Check if we already have the config and aren't forcing a refresh
      const cachedConfig = get().configs[effectiveChatbotId];
      if (!forceRefresh && cachedConfig) {
        return cachedConfig;
      }

      set({ isLoading: true, error: null });

      // Fetch from database
      const { data, error } = await supabase
        .from('chatbot_configs')
        .select('*')
        .eq('chatbot_public_id', effectiveChatbotId)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          // No data found
          set({ isLoading: false });
          return null;
        }
        throw error;
      }

      if (!data) {
        set({ isLoading: false });
        return null;
      }

      // Update store
      set((state) => ({
        configs: {
          ...state.configs,
          [effectiveChatbotId]: data as ChatbotConfig
        },
        isLoading: false
      }));

      return data as ChatbotConfig;
    } catch (err) {
      console.error('Error fetching chatbot config:', err);
      set({ error: err as Error, isLoading: false });
      return null;
    }
  },
  
  getConfigSection: async <T>(
    sectionKey: keyof Omit<ChatbotConfig, 'id' | 'chatbot_public_id' | 'created_at' | 'updated_at'>,
    chatbotPublicId?: string,
    forceRefresh = false
  ): Promise<T | null> => {
    const config = await get().fetchConfig(chatbotPublicId, forceRefresh);
    if (!config || !config[sectionKey]) {
      return null;
    }
    return config[sectionKey] as unknown as T;
  },
  
  updateConfigSection: async <T extends object | string | number | boolean | null>(
    sectionKey: keyof Omit<ChatbotConfig, 'id' | 'chatbot_public_id' | 'created_at' | 'updated_at'>,
    data: T,
    chatbotPublicId?: string
  ): Promise<boolean> => {
    try {
      // Get chatbotPublicId if not provided - fixed the || operation that was causing the issue
      let effectiveChatbotId: string;
      
      if (chatbotPublicId) {
        effectiveChatbotId = chatbotPublicId;
      } else {
        const fetchedId = await getChatbotPublicId();
        if (!fetchedId) {
          console.error('No chatbotPublicId available for config update');
          return false;
        }
        effectiveChatbotId = fetchedId;
      }

      set({ isLoading: true, error: null });

      // Get the current config first (to check if it exists)
      const currentConfig = await get().fetchConfig(effectiveChatbotId, true);
      
      if (currentConfig) {
        // Update existing config
        const { error } = await supabase
          .from('chatbot_configs')
          .update({ [sectionKey]: data as Json })
          .eq('chatbot_public_id', effectiveChatbotId);

        if (error) throw error;
      } else {
        // Create new config
        const { error } = await supabase
          .from('chatbot_configs')
          .insert({
            chatbot_public_id: effectiveChatbotId,
            [sectionKey]: data as Json
          });

        if (error) throw error;
      }

      // Update store with new data
      set((state) => {
        const existingConfig = state.configs[effectiveChatbotId] || { chatbot_public_id: effectiveChatbotId } as Partial<ChatbotConfig>;
        const updatedConfig = {
          ...existingConfig,
          [sectionKey]: data as Json
        };
        
        return {
          configs: {
            ...state.configs,
            [effectiveChatbotId]: updatedConfig as ChatbotConfig
          },
          isLoading: false
        };
      });

      return true;
    } catch (err) {
      console.error(`Error updating chatbot config section ${sectionKey}:`, err);
      set({ error: err as Error, isLoading: false });
      return false;
    }
  },
  
  clearConfig: (chatbotPublicId?: string) => {
    if (chatbotPublicId) {
      set((state) => {
        const newConfigs = { ...state.configs };
        delete newConfigs[chatbotPublicId];
        return { configs: newConfigs };
      });
    } else {
      // Clear all configs
      set({ configs: {} });
    }
  }
})); 